/**
 * NavigationConfigPanel Component for UI-Model-1
 *
 * Configuration panel for editing navigation bar settings.
 * Shows when the navigation bar is selected in the NavigationPreview.
 *
 * Settings include:
 * - showNavigation toggle
 * - orientation (Horizontal/Vertical)
 * - style (Block/Stepper)
 * - flow (Linear/Nonlinear)
 * - showDivider checkbox
 * - pageSpacing slider
 * - groupSpacing slider
 * - Added Groups/Pages section with drag-drop reordering
 */

import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Slider from 'Components/DesignSystem/Slider/Slider';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import {
  createUpdateNavigationConfigCommand,
  createRemoveGroupCommand,
  createRemovePageCommand,
  createReorderGroupsCommand,
  createReorderPagesCommand,
  createMovePageBetweenGroupsCommand,
  createDuplicatePageCommand,
} from '../../types/command.types';
import { NavigationConfigAPI, GroupAPI, PageAPI } from '../../types/state.types';
import { DropdownMenu, DropdownMenuItem } from '../Common/DropdownMenu';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import { SaveDataByType } from '../../services/SaveQueue';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';

interface NavigationConfigPanelProps {
  id?: string;
}

// Icons for menu items
const DragHandleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tw-text-ink-secondary" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.5 3.5C5.5 4.05228 5.05228 4.5 4.5 4.5C3.94772 4.5 3.5 4.05228 3.5 3.5C3.5 2.94772 3.94772 2.5 4.5 2.5C5.05228 2.5 5.5 2.94772 5.5 3.5Z" fill="currentColor"/>
    <path d="M5.5 8C5.5 8.55228 5.05228 9 4.5 9C3.94772 9 3.5 8.55228 3.5 8C3.5 7.44772 3.94772 7 4.5 7C5.05228 7 5.5 7.44772 5.5 8Z" fill="currentColor"/>
    <path d="M5.5 12.5C5.5 13.0523 5.05228 13.5 4.5 13.5C3.94772 13.5 3.5 13.0523 3.5 12.5C3.5 11.9477 3.94772 11.5 4.5 11.5C5.05228 11.5 5.5 11.9477 5.5 12.5Z" fill="currentColor"/>
    <path d="M12.5 3.5C12.5 4.05228 12.0523 4.5 11.5 4.5C10.9477 4.5 10.5 4.05228 10.5 3.5C10.5 2.94772 10.9477 2.5 11.5 2.5C12.0523 2.5 12.5 2.94772 12.5 3.5Z" fill="currentColor"/>
    <path d="M12.5 8C12.5 8.55228 12.0523 9 11.5 9C10.9477 9 10.5 8.55228 10.5 8C10.5 7.44772 10.9477 7 11.5 7C12.0523 7 12.5 7.44772 12.5 8Z" fill="currentColor"/>
    <path d="M12.5 12.5C12.5 13.0523 12.0523 13.5 11.5 13.5C10.9477 13.5 10.5 13.0523 10.5 12.5C10.5 11.9477 10.9477 11.5 11.5 11.5C12.0523 11.5 12.5 11.9477 12.5 12.5Z" fill="currentColor"/>
  </svg>
);

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tw-text-ink-secondary" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 9C8.55228 9 9 8.55228 9 8C9 7.44772 8.55228 7 8 7C7.44772 7 7 7.44772 7 8C7 8.55228 7.44772 9 8 9Z" fill="currentColor"/>
    <path d="M8 5C8.55228 5 9 4.55228 9 4C9 3.44772 8.55228 3 8 3C7.44772 3 7 3.44772 7 4C7 4.55228 7.44772 5 8 5Z" fill="currentColor"/>
    <path d="M8 13C8.55228 13 9 12.5523 9 12C9 11.4477 8.55228 11 8 11C7.44772 11 7 11.4477 7 12C7 12.5523 7.44772 13 8 13Z" fill="currentColor"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.5 1.5H8.5M1.5 3.5H12.5M11.167 3.5L10.6 10.7667C10.5333 11.5667 9.86667 12.1667 9.06667 12.1667H4.93333C4.13333 12.1667 3.46667 11.5667 3.4 10.7667L2.83333 3.5M5.83333 6.16667V9.16667M8.16667 6.16667V9.16667" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DuplicateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.33333 4.66667V2.33333C9.33333 1.59695 8.73638 1 8 1H2.33333C1.59695 1 1 1.59695 1 2.33333V8C1 8.73638 1.59695 9.33333 2.33333 9.33333H4.66667M6 4.66667H11.6667C12.403 4.66667 13 5.26362 13 6V11.6667C13 12.403 12.403 13 11.6667 13H6C5.26362 13 4.66667 12.403 4.66667 11.6667V6C4.66667 5.26362 5.26362 4.66667 6 4.66667Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tw-text-ink-secondary" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4.5C2 3.67157 2.67157 3 3.5 3H5.58579C5.851 3 6.10536 3.10536 6.29289 3.29289L7.70711 4.70711C7.89464 4.89464 8.149 5 8.41421 5H12.5C13.3284 5 14 5.67157 14 6.5V11.5C14 12.3284 13.3284 13 12.5 13H3.5C2.67157 13 2 12.3284 2 11.5V4.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tw-text-ink-secondary" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 1.5H4C3.17157 1.5 2.5 2.17157 2.5 3V13C2.5 13.8284 3.17157 14.5 4 14.5H12C12.8284 14.5 13.5 13.8284 13.5 13V6M9 1.5L13.5 6M9 1.5V6H13.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDownIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`tw-text-ink-secondary tw-transition-transform tw-duration-200 ${isExpanded ? 'tw-rotate-180' : ''}`}
  >
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Delete confirmation state type
interface DeleteConfirmation {
  isOpen: boolean;
  type: 'group' | 'page' | null;
  groupId: string | null;
  pageId: string | null;
  name: string;
}

/**
 * Main NavigationConfigPanel Component
 */
export const NavigationConfigPanel = ({ id }: NavigationConfigPanelProps) => {
  const baseId = id || 'navigation-config-panel';
  const { state, dispatch } = useUIModelState();
  const { deleteCanvas, bulkDeleteCanvases } = useUIModelApi();
  const { enqueueSave } = useSaveQueue();
  const navigation = state.model?.layout.navigation;

  // Debounce refs for spacing sliders
  const pageSpacingTimerRef = useRef<NodeJS.Timeout>(null);
  const groupSpacingTimerRef = useRef<NodeJS.Timeout>(null);

  // Expanded state for groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Enqueue navigation save when config changes
  // Accepts optional navigationData override to avoid stale closure issues
  const enqueueNavigationSave = useCallback((navigationData?: typeof navigation) => {
    const navToSave = navigationData ?? navigation;
    if (!navToSave) return;

    const saveData: SaveDataByType['navigation'] = {
      navigationData: navToSave,
    };

    enqueueSave('navigation', saveData);
  }, [navigation, enqueueSave]);

  // Delete confirmation dialog state
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
    type: null,
    groupId: null,
    pageId: null,
    name: '',
  });

  // Get sorted groups
  const sortedGroups = useMemo(() => {
    if (!navigation?.groups) return [];
    return [...navigation.groups].sort((a, b) => a.order - b.order);
  }, [navigation?.groups]);

  // Extract current config values from root level only (API-Direct architecture)
  // State structure matches API format - no nested configuration object
  const config = {
    showNavigation: navigation?.showNavigation ?? true,
    orientation: navigation?.orientation ?? 'Horizontal',
    style: navigation?.style ?? 'Block',
    flow: navigation?.flow ?? 'Nonlinear',
    showDivider: navigation?.showDivider ?? true,
    pageSpacing: navigation?.pageSpacing ?? 8,
    groupSpacing: navigation?.groupSpacing ?? 16,
    incompleteOpeningPage: navigation?.incompleteOpeningPage ?? 'first-incomplete-page',
    completeOpeningPage: navigation?.completeOpeningPage ?? 'first-page-always',
  };

  // Local state for spacing values (for responsive slider updates)
  const [localPageSpacing, setLocalPageSpacing] = useState<number>(config.pageSpacing);
  const [localGroupSpacing, setLocalGroupSpacing] = useState<number>(config.groupSpacing);

  // Sync local state with config changes
  useEffect(() => {
    setLocalPageSpacing(config.pageSpacing);
    setLocalGroupSpacing(config.groupSpacing);
  }, [config.pageSpacing, config.groupSpacing]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pageSpacingTimerRef.current) {
        clearTimeout(pageSpacingTimerRef.current);
      }
      if (groupSpacingTimerRef.current) {
        clearTimeout(groupSpacingTimerRef.current);
      }
    };
  }, []);

  // Helper to dispatch navigation config updates
  const updateNavConfig = useCallback(
    (updates: Partial<NavigationConfigAPI>, description?: string) => {
      const command = createUpdateNavigationConfigCommand(
        updates,
        description || 'Update navigation configuration'
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

      // Merge updates with current navigation to avoid stale closure issue
      // The updates object contains the values we just dispatched, so we apply
      // them immediately rather than waiting for state to update
      if (navigation) {
        const updatedNavigation = {
          ...navigation,
          ...updates, // Apply the updates we just dispatched
        };
        const saveData: SaveDataByType['navigation'] = {
          navigationData: updatedNavigation,
        };
        enqueueSave('navigation', saveData);
      }
    },
    [dispatch, navigation, enqueueSave]
  );

  // Toggle group expansion
  const toggleGroupExpansion = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  // Generate unique ID
  const generateId = useCallback((prefix: string): string => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, type } = result;

      // Dropped outside a droppable area
      if (!destination || !navigation) return;

      // No movement
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      if (type === 'GROUP') {
        // Reorder groups
        const newGroupIds = sortedGroups.map((g) => g.id);
        const [movedId] = newGroupIds.splice(source.index, 1);
        newGroupIds.splice(destination.index, 0, movedId);

        const command = createReorderGroupsCommand(
          newGroupIds,
          'Reorder groups'
        );
        dispatch({ type: 'EXECUTE_COMMAND', payload: command });
        dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

        // Compute the expected navigation state with reordered groups
        const reorderedGroups = newGroupIds.map((id, index) => {
          const group = navigation.groups.find((g) => g.id === id);
          return group ? { ...group, order: index } : null;
        }).filter(Boolean) as typeof navigation.groups;
        enqueueNavigationSave({ ...navigation, groups: reorderedGroups });
      } else if (type === 'PAGE') {
        const sourceGroupId = source.droppableId.replace('pages-', '');
        const destGroupId = destination.droppableId.replace('pages-', '');

        if (sourceGroupId === destGroupId) {
          // Reorder within same group
          const group = sortedGroups.find((g) => g.id === sourceGroupId);
          if (!group) return;

          const sortedPages = [...group.pages].sort((a, b) => a.order - b.order);
          const newPageIds = sortedPages.map((p) => p.id);
          const [movedId] = newPageIds.splice(source.index, 1);
          newPageIds.splice(destination.index, 0, movedId);

          const command = createReorderPagesCommand(
            sourceGroupId,
            newPageIds,
            'Reorder pages'
          );
          dispatch({ type: 'EXECUTE_COMMAND', payload: command });
          dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

          // Compute the expected navigation state with reordered pages
          const updatedGroups = navigation.groups.map((g) => {
            if (g.id !== sourceGroupId) return g;
            const reorderedPages = newPageIds.map((pageId, index) => {
              const page = g.pages.find((p) => p.id === pageId);
              return page ? { ...page, order: index } : null;
            }).filter(Boolean) as typeof g.pages;
            return { ...g, pages: reorderedPages };
          });
          enqueueNavigationSave({ ...navigation, groups: updatedGroups });
        } else {
          // Move between groups
          const sourceGroup = sortedGroups.find((g) => g.id === sourceGroupId);
          if (!sourceGroup) return;

          const sortedSourcePages = [...sourceGroup.pages].sort((a, b) => a.order - b.order);
          const pageId = sortedSourcePages[source.index]?.id;
          if (!pageId) return;

          const command = createMovePageBetweenGroupsCommand(
            pageId,
            sourceGroupId,
            destGroupId,
            destination.index,
            'Move page to different group'
          );
          dispatch({ type: 'EXECUTE_COMMAND', payload: command });
          dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

          // Compute the expected navigation state with moved page
          const movedPage = sourceGroup.pages.find((p) => p.id === pageId);
          if (!movedPage) return;

          const updatedGroups = navigation.groups.map((g) => {
            if (g.id === sourceGroupId) {
              // Remove page from source group and reorder
              const remainingPages = g.pages
                .filter((p) => p.id !== pageId)
                .sort((a, b) => a.order - b.order)
                .map((p, index) => ({ ...p, order: index }));
              return { ...g, pages: remainingPages };
            }
            if (g.id === destGroupId) {
              // Add page to destination group at specified index
              const destPages = [...g.pages].sort((a, b) => a.order - b.order);
              const newPage = { ...movedPage, order: destination.index };
              destPages.splice(destination.index, 0, newPage);
              // Reorder all pages
              const reorderedPages = destPages.map((p, index) => ({ ...p, order: index }));
              return { ...g, pages: reorderedPages };
            }
            return g;
          });
          enqueueNavigationSave({ ...navigation, groups: updatedGroups });
        }
      }
    },
    [dispatch, sortedGroups, navigation, enqueueNavigationSave]
  );

  // Handle delete group
  const handleDeleteGroup = useCallback(
    (group: GroupAPI) => {
      setDeleteConfirmation({
        isOpen: true,
        type: 'group',
        groupId: group.id,
        pageId: null,
        name: group.name,
      });
    },
    []
  );

  // Handle delete page
  const handleDeletePage = useCallback(
    (page: PageAPI, groupId: string) => {
      setDeleteConfirmation({
        isOpen: true,
        type: 'page',
        groupId,
        pageId: page.id,
        name: page.name,
      });
    },
    []
  );

  // Handle duplicate page
  const handleDuplicatePage = useCallback(
    (page: PageAPI, groupId: string) => {
      if (!navigation) return;

      const newPageId = generateId('page');
      const newCanvasId = generateId('canvas');

      const command = createDuplicatePageCommand(
        page.id,
        groupId,
        newPageId,
        newCanvasId,
        `Duplicate page: ${page.name}`
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

      // Compute the expected navigation state with duplicated page
      const updatedGroups = navigation.groups.map((g) => {
        if (g.id !== groupId) return g;
        const sortedPages = [...g.pages].sort((a, b) => a.order - b.order);
        const originalPageIndex = sortedPages.findIndex((p) => p.id === page.id);

        // Generate unique copy name
        const existingNames = new Set(g.pages.map(p => p.name));
        let copyName = `${page.name} (Copy)`;
        let copyCounter = 2;
        while (existingNames.has(copyName)) {
          copyName = `${page.name} (Copy ${copyCounter})`;
          copyCounter++;
        }

        const duplicatedPage: PageAPI = {
          ...page,
          id: newPageId,
          canvasId: newCanvasId,
          name: copyName,
          order: originalPageIndex + 1,
        };
        // Insert after original and reorder
        const newPages = [...sortedPages];
        newPages.splice(originalPageIndex + 1, 0, duplicatedPage);
        const reorderedPages = newPages.map((p, index) => ({ ...p, order: index }));
        return { ...g, pages: reorderedPages };
      });
      enqueueNavigationSave({ ...navigation, groups: updatedGroups });
    },
    [dispatch, generateId, navigation, enqueueNavigationSave]
  );

  // Confirm delete action
  const confirmDelete = useCallback(() => {
    if (!navigation) {
      setDeleteConfirmation({
        isOpen: false,
        type: null,
        groupId: null,
        pageId: null,
        name: '',
      });
      return;
    }

    if (deleteConfirmation.type === 'group' && deleteConfirmation.groupId) {
      // Capture group's pages BEFORE dispatch removes them
      const deletedGroup = navigation.groups.find(g => g.id === deleteConfirmation.groupId);

      const command = createRemoveGroupCommand(
        deleteConfirmation.groupId,
        `Delete group: ${deleteConfirmation.name}`
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

      // Compute the expected navigation state with group removed
      const updatedGroups = navigation.groups
        .filter((g) => g.id !== deleteConfirmation.groupId)
        .map((g, index) => ({ ...g, order: index }));
      enqueueNavigationSave({ ...navigation, groups: updatedGroups });

      // Bulk delete all canvases for pages in this group via API
      if (deletedGroup) {
        const canvasIds = deletedGroup.pages.map(page =>
          page.canvasId || `${deleteConfirmation.groupId}_${page.id}`
        );
        if (canvasIds.length > 0) {
          bulkDeleteCanvases(canvasIds, state.model?.id).catch(err =>
            console.error('Failed to bulk delete canvases:', err)
          );
        }
      }

      // Auto-select previous group (or next if first was deleted)
      const remainingGroups = navigation.groups
        .filter(g => g.id !== deleteConfirmation.groupId)
        .sort((a, b) => a.order - b.order);

      if (remainingGroups.length > 0) {
        const originalSorted = [...navigation.groups].sort((a, b) => a.order - b.order);
        const deletedIndex = originalSorted.findIndex(g => g.id === deleteConfirmation.groupId);
        const targetIndex = Math.min(Math.max(deletedIndex - 1, 0), remainingGroups.length - 1);
        const targetGroup = remainingGroups[targetIndex];
        const firstPage = [...targetGroup.pages].sort((a, b) => a.order - b.order)[0];
        if (firstPage) {
          dispatch({ type: 'SET_ACTIVE_GROUP', payload: { groupId: targetGroup.id } });
          dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId: firstPage.id } });
        }
      }
    } else if (deleteConfirmation.type === 'page' && deleteConfirmation.pageId && deleteConfirmation.groupId) {
      // Read page.canvasId from navigation state BEFORE dispatch removes it
      const group = navigation.groups.find(g => g.id === deleteConfirmation.groupId);
      const page = group?.pages.find(p => p.id === deleteConfirmation.pageId);
      const canvasId = page?.canvasId || `${deleteConfirmation.groupId}_${deleteConfirmation.pageId}`;

      const command = createRemovePageCommand(
        deleteConfirmation.pageId,
        deleteConfirmation.groupId,
        `Delete page: ${deleteConfirmation.name}`
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

      // Compute the expected navigation state with page removed
      const updatedGroups = navigation.groups.map((g) => {
        if (g.id !== deleteConfirmation.groupId) return g;
        const remainingPages = g.pages
          .filter((p) => p.id !== deleteConfirmation.pageId)
          .sort((a, b) => a.order - b.order)
          .map((p, index) => ({ ...p, order: index }));
        return { ...g, pages: remainingPages };
      });
      enqueueNavigationSave({ ...navigation, groups: updatedGroups });

      // Delete canvas via API (fire-and-forget, matching NavigationContext pattern)
      deleteCanvas(canvasId, state.model?.id).catch(err => console.error('Failed to delete canvas:', err));

      // Auto-select previous page (or next if first was deleted)
      if (group) {
        const sortedPages = [...group.pages]
          .filter(p => p.id !== deleteConfirmation.pageId)
          .sort((a, b) => a.order - b.order);

        if (sortedPages.length > 0) {
          const originalSorted = [...group.pages].sort((a, b) => a.order - b.order);
          const deletedIndex = originalSorted.findIndex(p => p.id === deleteConfirmation.pageId);
          const targetIndex = Math.min(Math.max(deletedIndex - 1, 0), sortedPages.length - 1);
          const targetPage = sortedPages[targetIndex];
          dispatch({ type: 'SET_ACTIVE_GROUP', payload: { groupId: deleteConfirmation.groupId } });
          dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId: targetPage.id } });
        }
      }
    }

    setDeleteConfirmation({
      isOpen: false,
      type: null,
      groupId: null,
      pageId: null,
      name: '',
    });
  }, [deleteConfirmation, dispatch, navigation, enqueueNavigationSave, deleteCanvas, bulkDeleteCanvases, state.model?.id]);

  // Cancel delete action
  const cancelDelete = useCallback(() => {
    setDeleteConfirmation({
      isOpen: false,
      type: null,
      groupId: null,
      pageId: null,
      name: '',
    });
  }, []);

  // Get group menu items
  const getGroupMenuItems = useCallback(
    (group: GroupAPI): DropdownMenuItem[] => {
      const items: DropdownMenuItem[] = [];

      // Only show delete if there's more than one group
      if (sortedGroups.length > 1) {
        items.push({
          id: 'delete-group',
          label: 'Delete Group',
          icon: <TrashIcon />,
          onClick: () => handleDeleteGroup(group),
          className: 'tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint',
        });
      }

      return items;
    },
    [handleDeleteGroup, sortedGroups.length]
  );

  // Get page menu items
  const getPageMenuItems = useCallback(
    (page: PageAPI, groupId: string, pageCount: number): DropdownMenuItem[] => {
      const items: DropdownMenuItem[] = [
        {
          id: 'duplicate-page',
          label: 'Duplicate Page',
          icon: <DuplicateIcon />,
          onClick: () => handleDuplicatePage(page, groupId),
        },
      ];

      // Only show delete if there's more than one page in the group
      if (pageCount > 1) {
        items.push({
          id: 'delete-page',
          label: 'Delete Page',
          icon: <TrashIcon />,
          onClick: () => handleDeletePage(page, groupId),
          className: 'tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint',
          divider: true,
        });
      }

      return items;
    },
    [handleDeletePage, handleDuplicatePage]
  );

  // Debounced handlers for spacing changes
  const handlePageSpacingChange = useCallback(
    (value: number) => {
      setLocalPageSpacing(value);

      if (pageSpacingTimerRef.current) {
        clearTimeout(pageSpacingTimerRef.current);
      }

      pageSpacingTimerRef.current = setTimeout(() => {
        updateNavConfig({ pageSpacing: value }, `Set page spacing to ${value}px`);
      }, 500);
    },
    [updateNavConfig]
  );

  const handleGroupSpacingChange = useCallback(
    (value: number) => {
      setLocalGroupSpacing(value);

      if (groupSpacingTimerRef.current) {
        clearTimeout(groupSpacingTimerRef.current);
      }

      groupSpacingTimerRef.current = setTimeout(() => {
        updateNavConfig({ groupSpacing: value }, `Set group spacing to ${value}px`);
      }, 500);
    },
    [updateNavConfig]
  );

  if (!navigation) {
    return (
      <div id={baseId} data-testid={baseId} className="tw-p-4 tw-text-ink-secondary tw-text-sm">
        No navigation configuration available
      </div>
    );
  }

  const isDisabled = !config.showNavigation;

  return (
    <div id={baseId} data-testid={baseId} className="tw-space-y-6">
      {/* Navigation row - label on left, toggle on right (matching HeaderConfigPanel pattern) */}
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-4 tw-border-b tw-border-edge">
        <span className="tw-text-title tw-text-ink">Navigation</span>
        <Toggle
          label=""
          checked={config.showNavigation}
          onChange={(checked) =>
            updateNavConfig(
              { showNavigation: checked },
              `${checked ? 'Show' : 'Hide'} navigation`
            )
          }
        />
      </div>

      {/* Section: Layout Settings */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50' : ''}`}>
        <FieldTitle label="Layout Settings" size="sm" />

        {/* Orientation Radio Group */}
        <div className="tw-space-y-2">
          <FieldTitle label="Orientation" size="sm" disabled />
          <div className="tw-flex tw-gap-4">
            <Radio
              label="Horizontal"
              value="Horizontal"
              checked={config.orientation === 'Horizontal'}
              onChange={() =>
                updateNavConfig(
                  { orientation: 'Horizontal' },
                  'Set orientation to Horizontal'
                )
              }
              disabled
              name={`${baseId}-orientation`}
            />
            <Radio
              label="Vertical"
              value="Vertical"
              checked={config.orientation === 'Vertical'}
              onChange={() =>
                updateNavConfig(
                  { orientation: 'Vertical' },
                  'Set orientation to Vertical'
                )
              }
              disabled
              name={`${baseId}-orientation`}
            />
          </div>
        </div>

        {/* Style Radio Group */}
        <div className="tw-space-y-2">
          <FieldTitle label="Style" size="sm" />
          <div className="tw-flex tw-gap-4">
            <Radio
              label="Block"
              value="Block"
              checked={config.style === 'Block'}
              onChange={() =>
                updateNavConfig(
                  { style: 'Block' },
                  'Set style to Block'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-style`}
            />
            <Radio
              label="Stepper"
              value="Stepper"
              checked={config.style === 'Stepper'}
              onChange={() =>
                updateNavConfig(
                  { style: 'Stepper' },
                  'Set style to Stepper'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-style`}
            />
          </div>
        </div>

        {/* Flow Radio Group */}
        <div className="tw-space-y-2">
          <FieldTitle label="Flow" size="sm" />
          <div className="tw-flex tw-gap-4">
            <Radio
              label="Linear"
              value="Linear"
              checked={config.flow === 'Linear'}
              onChange={() =>
                updateNavConfig(
                  { flow: 'Linear' },
                  'Set flow to Linear'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-flow`}
            />
            <Radio
              label="Nonlinear"
              value="Nonlinear"
              checked={config.flow === 'Nonlinear'}
              onChange={() =>
                updateNavConfig(
                  { flow: 'Nonlinear' },
                  'Set flow to Nonlinear'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-flow`}
            />
          </div>
        </div>
      </div>

      {/* Section: Opening Page */}
      <div className={`tw-space-y-3 ${isDisabled ? 'tw-opacity-50' : ''}`}>
        <FieldTitle
          label="Landing page on relaunch"
          size="sm"
          infoMessage="Controls which page the user lands on when the builder is reopened, separately for incomplete and complete configurations."
        />

        {/* When configuration is incomplete */}
        <div className="tw-space-y-2">
          <span className="tw-text-sub-title-label tw-text-ink-secondary tw-font-semibold tw-text-xs">
            When configuration is incomplete
          </span>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Radio
              label="First page always"
              value="first-page-always"
              checked={config.incompleteOpeningPage === 'first-page-always'}
              onChange={() =>
                updateNavConfig(
                  { incompleteOpeningPage: 'first-page-always' },
                  'Set incomplete opening page to first page always'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-incomplete-opening-page`}
            />
            <InfoTooltip
              label="About First page always (incomplete)"
              data-testid={`${baseId}-incomplete-first-page-always-info`}
              message="Always opens on page 1 when the configuration is incomplete, regardless of how far the user progressed."
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Radio
              label="First incomplete page"
              value="first-incomplete-page"
              checked={config.incompleteOpeningPage === 'first-incomplete-page'}
              onChange={() =>
                updateNavConfig(
                  { incompleteOpeningPage: 'first-incomplete-page' },
                  'Set incomplete opening page to first incomplete page'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-incomplete-opening-page`}
            />
            <InfoTooltip
              label="About First incomplete page"
              data-testid={`${baseId}-incomplete-first-incomplete-page-info`}
              message="Opens on the first page where required fields or categories have not yet been completed. Completed pages are skipped."
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Radio
              label="First page with a selected product"
              value="first-page-with-selected-product"
              checked={config.incompleteOpeningPage === 'first-page-with-selected-product'}
              onChange={() =>
                updateNavConfig(
                  { incompleteOpeningPage: 'first-page-with-selected-product' },
                  'Set incomplete opening page to first page with selected product'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-incomplete-opening-page`}
            />
            <InfoTooltip
              label="About First page with a selected product (incomplete)"
              data-testid={`${baseId}-incomplete-first-selected-page-info`}
              message="Opens on the first page that has at least one selected product. Guided Selling pages (Q&A only, no products) are skipped."
            />
          </div>
        </div>

        {/* When configuration is complete */}
        <div className="tw-space-y-2">
          <span className="tw-text-sub-title-label tw-text-ink-secondary tw-font-semibold tw-text-xs">
            When configuration is complete
          </span>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Radio
              label="First page always"
              value="first-page-always"
              checked={config.completeOpeningPage === 'first-page-always'}
              onChange={() =>
                updateNavConfig(
                  { completeOpeningPage: 'first-page-always' },
                  'Set complete opening page to first page always'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-complete-opening-page`}
            />
            <InfoTooltip
              label="About First page always (complete)"
              data-testid={`${baseId}-complete-first-page-always-info`}
              message="When the configuration is complete, always opens on page 1."
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Radio
              label="First page with a selected product"
              value="first-page-with-selected-product"
              checked={config.completeOpeningPage === 'first-page-with-selected-product'}
              onChange={() =>
                updateNavConfig(
                  { completeOpeningPage: 'first-page-with-selected-product' },
                  'Set complete opening page to first page with selected product'
                )
              }
              disabled={isDisabled}
              name={`${baseId}-complete-opening-page`}
            />
            <InfoTooltip
              label="About First page with a selected product (complete)"
              data-testid={`${baseId}-complete-first-selected-page-info`}
              message="When the configuration is complete, opens on the first page that has at least one selected product. Guided Selling pages (Q&A only, no products) are skipped."
            />
          </div>
        </div>
      </div>

      {/* Section: Divider */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50' : ''}`}>
        <FieldTitle label="Divider" size="sm" />

        <Checkbox
          label="Show Divider between groups"
          checked={config.showDivider}
          onChange={(checked) =>
            updateNavConfig(
              { showDivider: checked },
              `${checked ? 'Show' : 'Hide'} divider`
            )
          }
          disabled={isDisabled}
        />
      </div>

      {/* Section: Spacing */}
      <div className={`tw-space-y-4 ${isDisabled ? 'tw-opacity-50' : ''}`}>
        <FieldTitle label="Spacing" size="sm" />

        {/* Page Spacing */}
        <div id={`${baseId}-page-spacing-section`}>
          <FieldTitle label="Page Spacing" size="sm" />
          <div className="tw-flex tw-items-center tw-space-x-2">
            {/*
              `data-testid` is FORWARDED, per instance.

              This is the pair that made the old shared component's hardcoded
              `data-testid="components-slider-input-1"` a live bug rather than a
              latent one: page spacing and group spacing are unconditional siblings
              in this section, so one testid matched two elements and `getByTestId`
              threw here. `hideLabel` keeps the existing "Page Spacing" FieldTitle
              as the visible label while giving the control the name it never had.
            */}
            <Slider
              label="Page Spacing"
              hideLabel
              size="sm"
              formatValue={(spacing) => `${spacing}px`}
              data-testid={`${baseId}-page-spacing-slider`}
              disabled={isDisabled}
              value={localPageSpacing}
              onChange={handlePageSpacingChange}
              min={4}
              max={24}
            />
            <div className="tw-flex tw-items-center tw-text-ink">
              <input
                id={`${baseId}-page-spacing-input`}
                data-testid={`${baseId}-page-spacing-input`}
                type="number"
                value={localPageSpacing}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || e.target.value === '') {
                    handlePageSpacingChange(4);
                    return;
                  }
                  const clampedValue = Math.min(Math.max(value, 4), 24);
                  handlePageSpacingChange(clampedValue);
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < 4) {
                    handlePageSpacingChange(4);
                  } else if (value > 24) {
                    handlePageSpacingChange(24);
                  }
                }}
                min="4"
                max="24"
                disabled={isDisabled}
                className="tw-w-14 tw-border tw-border-edge-strong tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-bg-surface tw-text-ink"
              />
              <span className="tw-ml-2">px</span>
            </div>
          </div>
        </div>

        {/* Group Spacing */}
        <div id={`${baseId}-group-spacing-section`}>
          <FieldTitle label="Group Spacing" size="sm" />
          <div className="tw-flex tw-items-center tw-space-x-2">
            <Slider
              label="Group Spacing"
              hideLabel
              size="sm"
              formatValue={(spacing) => `${spacing}px`}
              data-testid={`${baseId}-group-spacing-slider`}
              disabled={isDisabled}
              value={localGroupSpacing}
              onChange={handleGroupSpacingChange}
              min={4}
              max={24}
            />
            <div className="tw-flex tw-items-center tw-text-ink">
              <input
                id={`${baseId}-group-spacing-input`}
                data-testid={`${baseId}-group-spacing-input`}
                type="number"
                value={localGroupSpacing}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || e.target.value === '') {
                    handleGroupSpacingChange(4);
                    return;
                  }
                  const clampedValue = Math.min(Math.max(value, 4), 24);
                  handleGroupSpacingChange(clampedValue);
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < 4) {
                    handleGroupSpacingChange(4);
                  } else if (value > 24) {
                    handleGroupSpacingChange(24);
                  }
                }}
                min="4"
                max="24"
                disabled={isDisabled}
                className="tw-w-14 tw-border tw-border-edge-strong tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-bg-surface tw-text-ink"
              />
              <span className="tw-ml-2">px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section: Added Groups/Pages */}
      <div className="tw-space-y-4">
        <FieldTitle label="Added Groups/Pages" size="sm" />

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="groups" type="GROUP">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                id={`${baseId}-groups-list`}
                className="tw-space-y-2"
              >
                {sortedGroups.map((group, groupIndex) => {
                  const isExpanded = expandedGroups.has(group.id);
                  const sortedPages = [...group.pages].sort((a, b) => a.order - b.order);
                  const groupMenuItems = getGroupMenuItems(group);

                  return (
                    <Draggable key={group.id} draggableId={group.id} index={groupIndex}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`tw-border tw-border-edge tw-rounded ${
                            snapshot.isDragging ? 'tw-shadow-lg tw-bg-surface-raised' : ''
                          }`}
                        >
                          {/* Group Header */}
                          <div
                            className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-surface-sunken tw-rounded-t"
                          >
                            <div
                              {...provided.dragHandleProps}
                              className="tw-cursor-grab tw-flex-shrink-0"
                            >
                              <DragHandleIcon />
                            </div>
                            <button aria-label="Expand" data-testid={`configpanel-navigationconfigpanel-button-1-${groupIndex}`}
                              onClick={() => toggleGroupExpansion(group.id)}
                              className="tw-flex-shrink-0 tw-p-1 hover:tw-bg-surface-hover tw-rounded"
                            >
                              <ChevronDownIcon isExpanded={isExpanded} />
                            </button>
                            <FolderIcon />
                            <span role="button" data-testid={`configpanel-navigationconfigpanel-span-1-${groupIndex}`}
                              className="tw-flex-1 tw-text-sm tw-font-medium tw-text-ink tw-truncate tw-cursor-pointer"
                              onClick={() => toggleGroupExpansion(group.id)}
                            >
                              {group.name}
                            </span>
                            <span className="tw-text-xs tw-text-ink-secondary">
                              {group.pages.length} page{group.pages.length !== 1 ? 's' : ''}
                            </span>
                            {groupMenuItems.length > 0 && (
                              <DropdownMenu
                                id={`${baseId}-group-menu-${group.id}`}
                                items={groupMenuItems}
                                trigger={
                                  <div className="tw-p-1 hover:tw-bg-surface-hover tw-rounded">
                                    <MoreIcon />
                                  </div>
                                }
                                position="right"
                              />
                            )}
                          </div>

                          {/* Pages List */}
                          {isExpanded && (
                            <Droppable droppableId={`pages-${group.id}`} type="PAGE">
                              {(pagesProvided) => (
                                <div
                                  ref={pagesProvided.innerRef}
                                  {...pagesProvided.droppableProps}
                                  className="tw-bg-surface tw-rounded-b"
                                >
                                  {sortedPages.map((page, pageIndex) => {
                                    const pageMenuItems = getPageMenuItems(page, group.id, sortedPages.length);

                                    return (
                                      <Draggable
                                        key={page.id}
                                        draggableId={page.id}
                                        index={pageIndex}
                                      >
                                        {(pageProvided, pageSnapshot) => (
                                          <div
                                            ref={pageProvided.innerRef}
                                            {...pageProvided.draggableProps}
                                            className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-pl-10 tw-border-t tw-border-edge ${
                                              pageSnapshot.isDragging
                                                ? 'tw-shadow-md tw-bg-accent-tint'
                                                : 'hover:tw-bg-surface-hover'
                                            }`}
                                          >
                                            <div
                                              {...pageProvided.dragHandleProps}
                                              className="tw-cursor-grab tw-flex-shrink-0"
                                            >
                                              <DragHandleIcon />
                                            </div>
                                            <PageIcon />
                                            <span className="tw-flex-1 tw-text-sm tw-text-ink tw-truncate">
                                              {page.name}
                                            </span>
                                            {pageMenuItems.length > 0 && (
                                              <DropdownMenu
                                                id={`${baseId}-page-menu-${page.id}`}
                                                items={pageMenuItems}
                                                trigger={
                                                  <div className="tw-p-1 hover:tw-bg-surface-hover tw-rounded">
                                                    <MoreIcon />
                                                  </div>
                                                }
                                                position="right"
                                              />
                                            )}
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {pagesProvided.placeholder}
                                  {sortedPages.length === 0 && (
                                    <div className="tw-px-3 tw-py-4 tw-pl-10 tw-text-sm tw-text-ink-secondary tw-text-center tw-border-t tw-border-edge">
                                      No pages in this group
                                    </div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {sortedGroups.length === 0 && (
          <div className="tw-text-sm tw-text-ink-secondary tw-text-center tw-py-4 tw-border tw-border-dashed tw-border-edge tw-rounded">
            No groups added yet
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        id={`${baseId}-delete-dialog`}
        isOpen={deleteConfirmation.isOpen}
        title={`Delete ${deleteConfirmation.type === 'group' ? 'Group' : 'Page'}`}
        message={
          deleteConfirmation.type === 'group'
            ? `Are you sure you want to delete the group "${deleteConfirmation.name}"? This will also delete all pages within this group.`
            : `Are you sure you want to delete the page "${deleteConfirmation.name}"?`
        }
        secondaryMessage={
          deleteConfirmation.type === 'group'
            ? 'This action cannot be undone.'
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="tw-bg-tone-danger-bold hover:tw-bg-tone-danger-bold-hover tw-text-ink-inverse"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default NavigationConfigPanel;
